<template>
    <div
        :class="{hstack: isHorizontal}"
        class="gap-2">
        <slot name="label"></slot>
        <Multiselect
            :model-value="selectValue"
            :class="{
                'is-valid': isComponentValid === true,
                'is-invalid': isComponentValid === false,
                'is-invalid': !!error,
            }"
            :options="selectItems"
            :mode="mode"
            :close-on-select="closeOnSelect"
            :label="'label'"
            :rules="getRules"
            :searchable="true"
            :filter-results="true"
            :strict="false"
            :submit="false"
            :disabled="isDisabled"
            :disabled-prop="'isDisabled'"
            :native-support="true"
            :can-clear="canClear"
            ref="multiselect"
            :hide-selected="hideSelected"
            :open-direction="openDirection"
            autocomplete="n/a"
            @change="handleChange($event)"
            @clear.prevent="handleClear"
            :classes="{
                tag: 'text-sm py-0.5 pl-2 rounded mr-1 mb-1 flex items-center tagwidth-100 text-truncate rtl:pl-0 rtl:pr-2 rtl:mr-0 rtl:ml-1',
            }">
            <template v-slot:beforelist>
                <button
                    type="button"
                    class="btn btn-link"
                    @click.prevent="handleSelectAll($event)"
                    v-if="includeShowAllOption">
                    Select All
                </button>
            </template>
            <template v-slot:multiplelabel="{values}">
                <div
                    class="multiselect-multiple-label"
                    v-if="values.length == 1">
                    {{ values[0].label }}
                </div>
                <div
                    class="multiselect-multiple-label"
                    v-else>
                    {{ values.length }} items selected
                </div>
            </template>
            <template v-slot:tag="{option, handleTagRemove, disabled}">
                <div
                    class="multiselect-tag"
                    :class="{
                        'is-disabled': disabled,
                    }">
                    <div
                        class="d-inlineblock hstack bg-secondary text-white text-sm py-0.5 pl-2 rounded mr-1 mb-1 flex items-center rtl:pl-0 rtl:pr-2 rtl:mr-0 rtl:ml-1"></div>
                    <span
                        tabindex="-1"
                        class="tagwidth-150 text-truncate">
                        {{ option.label }}
                    </span>
                    <span
                        v-if="!disabled"
                        class="multiselect-tag-remove"
                        @click.prevent="handleTagRemove(option, $event)">
                        <span class="multiselect-tag-remove-icon"></span>
                    </span>
                </div>
            </template>
        </Multiselect>
        <span
            class="invalid-feedback"
            role="alert"
            v-if="error">
            {{ error }}
        </span>
    </div>
</template>
<script lang="ts">
import {defineComponent, watch, ref, defineExpose} from "vue";
import type {PropType} from "vue";
import {SelectItem} from "../../types";
import Multiselect from "@vueform/multiselect";
import {isText} from "@/utils";
export default defineComponent({
    components: {
        Multiselect,
    },
    props: {
        selectItems: Object as PropType<SelectItem[]>,
        isDisabled: {
            readonly: false,
            default: false,
            type: Boolean,
        },
        modelValue: {
            type: Object as any,
            required: false,
        },
        isHorizontal: {
            readonly: false,
            default: false,
            type: Boolean,
        },
        isRequired: {
            readonly: false,
            default: false,
            type: Boolean,
        },
        mode: {
            default: "single",
            type: String,
        },
        min: {
            default: -1,
            type: Number,
        },
        max: {
            default: -1,
            type: Number,
        },
        isActionTaken: {
            default: false,
            type: Boolean,
        },
        isNullableBool: {
            default: false,
            type: Boolean,
        },
        includeShowAllOption: {
            default: false,
            type: Boolean,
        },
        closeOnSelect: {
            default: true,
            type: Boolean,
        },
        hideSelected: {
            default: true,
            type: Boolean,
        },
        openDirection: {
            default: "bottom",
            type: String,
        },
        canClear: {
            type: Boolean,
            default: true,
        },
        error: {
            type: String,
            default: null,
        },
    },
    computed: {
        getRules(): String[] {
            let rules = [] as String[];

            if (this.isRequired) {
                rules.push("required");
            }

            if (this.min != -1) {
                rules.push(`min:${this.min}`);
            }

            if (this.max != -1) {
                rules.push(`max:${this.max}`);
            }

            return rules;
        },
    },
    methods: {
        handleChange(e: any) {
            if (e) {
                let value = isText(e) || e?.value == undefined ? e : e.value;
                const regex = new RegExp("^[0-9]+$");
                if (this.isActionTaken && (value == "NONE" || value.indexOf("NONE") >= 0)) {
                    this.$emit("update:modelValue", "NONE");
                } else if (this.mode == "single") {
                    if (this.isNullableBool && value == -99) {
                        value = undefined;
                    }

                    this.$emit("update:modelValue", regex.test(value) ? Number(value) : value);
                } else if (value && !Array.isArray(value)) {
                    if (!regex.test(value)) {
                        let stringArray = [] as string[];

                        stringArray.push(value);

                        this.$emit("update:modelValue", stringArray);
                    } else {
                        let numberArray = [] as number[];

                        numberArray.push(Number(value));

                        this.$emit("update:modelValue", numberArray);
                    }
                } else {
                    this.$emit("update:modelValue", value);
                }
            } else {
                this.$emit("update:modelValue", undefined);
            }
        },
        handleClear() {
            this.selectValue = "";
            this.$emit("update:modelValue", this.selectValue);
            this.isValid();
        },
        handleSelectAll(e: any) {
            if (this.$refs.multiselect) {
                var multiselect = this.$refs.multiselect as Multiselect;
                multiselect.selectAll();
            }
        },
    },
    setup(props, context) {
        function getValue(value: any): any {
            if (props.mode != "single") {
                if (value == undefined) {
                    value = [];
                } else if (!Array.isArray(value)) {
                    value = [value];
                }

                //Only set the valid values as selected otherwise we end up with empty tags
                let result = [] as string[];

                [...value].map((item) => {
                    if (props.selectItems?.find((f) => f.value == item)) {
                        result.push(item);
                    }
                });

                if (value.length != result.length) {
                    context.emit("update:modelValue", result);
                }

                return result;
            }

            if (props.isNullableBool && value == undefined) {
                value = -99;
            }

            return value;
        }

        var selectValue = ref(getValue(props.modelValue));
        var isComponentValid = ref();
        const isValid = (): Boolean => {
            if (props.isRequired) {
                isComponentValid.value =
                    selectValue?.value != undefined && selectValue?.value != -1 && selectValue?.value.length != 0;
            } else {
                isComponentValid.value = true;
            }

            return isComponentValid.value;
        };

        defineExpose({isValid});

        return {selectValue, getValue, isValid, isComponentValid};
    },
    watch: {
        modelValue(newValue, oldValue) {
            this.selectValue = this.getValue(newValue);
        },
    },
});
</script>
<style src="@vueform/multiselect/themes/default.css"></style>
<style>
.form-select {
    padding: 0.5rem 2.25rem 0.5rem 0.75rem !important;
}

.multiselect-tag {
    background: var(--b2020-gray-200, #e8e8e8) !important;
    border-radius: 4px !important;
    color: #000000 !important;
    font-weight: initial !important;
}

.multiselect-dropdown .multiselect-options > .multiselect-option.is-pointed {
    border: 1px solid var(--b2020-info, #5bc0de);
    color: #000000 !important;
}

.multiselect-dropdown .multiselect-option.is-selected {
    background: var(--b2020-gray-200, #e8e8e8) !important;
    color: #000000 !important;
}
</style>
